import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const sellerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const meIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

function RecenterMap({ lat, lng }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], 13); }, [lat, lng, map]);
  return null;
}

const GeolocationComponent = ({ user }) => {
  const [myLocation, setMyLocation] = useState(null);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [nearbySellers, setNearbySellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoError, setGeoError] = useState(null);
  const [radius, setRadius] = useState(10);
  const [showUsers, setShowUsers] = useState(true);
  const [showSellers, setShowSellers] = useState(true);
  const [locating, setLocating] = useState(false);

  useEffect(() => { getLocation(); }, []);

  const getLocation = () => {
    setLocating(true);
    if (!navigator.geolocation) {
      setGeoError('Tu navegador no soporta geolocalizacion');
      setLoading(false);
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setMyLocation(loc);
        setGeoError(null);
        try {
          await axios.post(`${API}/geo/location/update`, { ...loc, visible: true });
          await fetchNearby(loc.latitude, loc.longitude);
        } catch (err) { console.error(err); }
        finally { setLoading(false); setLocating(false); }
      },
      (err) => {
        // Default to Caracas
        const defaultLoc = { latitude: 10.4806, longitude: -66.9036 };
        setMyLocation(defaultLoc);
        setGeoError('No se pudo obtener tu ubicacion. Mostrando Caracas por defecto.');
        setLoading(false);
        setLocating(false);
        fetchNearby(defaultLoc.latitude, defaultLoc.longitude);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const fetchNearby = async (lat, lng) => {
    try {
      const [usersRes, sellersRes] = await Promise.all([
        axios.get(`${API}/geo/nearby/users?latitude=${lat}&longitude=${lng}&radius_km=${radius}`),
        axios.get(`${API}/geo/nearby/sellers?latitude=${lat}&longitude=${lng}&radius_km=${radius}`)
      ]);
      setNearbyUsers(usersRes.data.nearby_users || []);
      setNearbySellers(sellersRes.data.nearby_sellers || []);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Obteniendo ubicacion...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="geo-module">
      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <button onClick={getLocation} disabled={locating} data-testid="refresh-location-btn"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 text-sm font-medium">
          {locating ? 'Localizando...' : 'Actualizar Ubicacion'}
        </button>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Radio:</label>
          <select value={radius} onChange={(e) => { setRadius(Number(e.target.value)); if (myLocation) fetchNearby(myLocation.latitude, myLocation.longitude); }}
            data-testid="radius-select" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
            <option value={5}>5 km</option>
            <option value={10}>10 km</option>
            <option value={20}>20 km</option>
            <option value={50}>50 km</option>
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={showUsers} onChange={(e) => setShowUsers(e.target.checked)} className="rounded" />
          <span className="text-blue-600">Usuarios ({nearbyUsers.length})</span>
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={showSellers} onChange={(e) => setShowSellers(e.target.checked)} className="rounded" />
          <span className="text-green-600">Vendedores ({nearbySellers.length})</span>
        </label>
        {geoError && <p className="text-xs text-amber-600 w-full">{geoError}</p>}
      </div>

      {/* Map */}
      {myLocation && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: '420px' }} data-testid="map-container">
          <MapContainer center={[myLocation.latitude, myLocation.longitude]} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            <RecenterMap lat={myLocation.latitude} lng={myLocation.longitude} />

            {/* My location */}
            <Marker position={[myLocation.latitude, myLocation.longitude]} icon={meIcon}>
              <Popup><strong>Tu ubicacion</strong></Popup>
            </Marker>

            {/* Radius circle */}
            <Circle center={[myLocation.latitude, myLocation.longitude]} radius={radius * 1000}
              pathOptions={{ color: '#6366f1', fillColor: '#6366f1', fillOpacity: 0.05, weight: 1 }} />

            {/* Nearby users */}
            {showUsers && nearbyUsers.map((u, i) => (
              <Marker key={`u-${i}`} position={[u.latitude, u.longitude]} icon={userIcon}>
                <Popup>
                  <div className="text-sm">
                    <strong>{u.user_name}</strong>
                    <p className="text-gray-500">{u.distance_km} km</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Nearby sellers */}
            {showSellers && nearbySellers.map((s, i) => (
              <Marker key={`s-${i}`} position={[s.latitude, s.longitude]} icon={sellerIcon}>
                <Popup>
                  <div className="text-sm">
                    <strong>{s.seller_name}</strong>
                    <p>{s.listing_title}</p>
                    <p className="font-semibold">{s.price} {s.currency}</p>
                    <p className="text-gray-500">{s.distance_km} km</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* Nearby list */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {showSellers && nearbySellers.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-green-50">
              <h3 className="text-sm font-semibold text-green-800">Vendedores Cercanos</h3>
            </div>
            <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
              {nearbySellers.map((s, i) => (
                <div key={i} className="px-4 py-2.5 flex justify-between items-center text-sm">
                  <div>
                    <p className="font-medium text-gray-900">{s.listing_title}</p>
                    <p className="text-xs text-gray-500">{s.seller_name} - {s.distance_km} km</p>
                  </div>
                  <span className="font-semibold text-green-700">{s.price} {s.currency}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {showUsers && nearbyUsers.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-blue-50">
              <h3 className="text-sm font-semibold text-blue-800">Usuarios Cercanos</h3>
            </div>
            <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
              {nearbyUsers.map((u, i) => (
                <div key={i} className="px-4 py-2.5 flex justify-between items-center text-sm">
                  <p className="font-medium text-gray-900">{u.user_name}</p>
                  <span className="text-xs text-gray-400">{u.distance_km} km</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeolocationComponent;
